/**
 * Fork volume modal component.
 * Prompts for a destination volume name and forks a data disk volume.
 *
 * @module components/ForkVolumeModal
 */

import { useState } from 'react';
import { Modal, Button, Input } from '@/components/ui';

interface ForkVolumeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onFork: (destinationVolumeName: string) => void;
  sourceVolumeName: string;
  isLoading?: boolean;
  error?: string | null;
}

export function ForkVolumeModal({
  isOpen,
  onClose,
  onFork,
  sourceVolumeName,
  isLoading = false,
  error = null,
}: ForkVolumeModalProps) {
  const [destinationName, setDestinationName] = useState('');
  const [nameError, setNameError] = useState('');

  const handleClose = () => {
    setDestinationName('');
    setNameError('');
    onClose();
  };

  const handleFork = () => {
    const trimmed = destinationName.trim();
    if (!trimmed) {
      setNameError('Volume name is required');
      return;
    }
    if (trimmed.toLowerCase() === sourceVolumeName.toLowerCase()) {
      setNameError('Destination name must differ from source');
      return;
    }
    setNameError('');
    onFork(trimmed);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Fork Data Disk Volume"
      description={`Create an independent copy of "${sourceVolumeName}"`}
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={handleClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleFork} isLoading={isLoading}>
            Fork
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Input
          label="Destination volume name"
          placeholder="my-volume-copy"
          value={destinationName}
          onChange={(e) => {
            setDestinationName(e.target.value);
            if (nameError) setNameError('');
          }}
          error={nameError}
          required
          autoFocus
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !isLoading) handleFork();
          }}
        />

        {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}
      </div>
    </Modal>
  );
}
